import { useEffect } from "react";
import {
  GoabSpacer,
  GoabGrid,
  GoabFormItem,
  GoabInputText,
  GoabDropdown,
  GoabDropdownItem,
  GoabRadioGroup,
  GoabRadioItem,
} from "@abgov/react-components";
import jexl from "jexl";
import {
  provinceList,
  toTitleCase,
  formatPostalCode,
} from "../../utils/globalUtility";
import CanadaPostGoabInput from "./CanadaPostGoabInput";
import { STREET_ADDRESS_POBOX_ERROR_RESOURCE_STRING } from "../../common/constants/schemas";

const getCountries = (lang = "en") => {
  const A = 65; // ASCII code for 'A'
  const Z = 90; // ASCII code for 'Z'
  const countryName = new Intl.DisplayNames([lang], { type: "region" });
  const countryList = [];

  for (let i = A; i <= Z; ++i) {
    for (let j = A; j <= Z; ++j) {
      const code = String.fromCharCode(i) + String.fromCharCode(j);
      const name = countryName.of(code);

      if (code !== name) {
        countryList.push({ label: name, code });
      }
    }
  }

  return countryList;
};

export const initializeCanadaAddressFields = (fields, data) => {
  fields["isPOBox"] = data["isPOBox"] ?? "";
  fields["streetAddressLine1"] = data["streetAddressLine1"] ?? "";
  fields["streetAddressLine2"] = data["streetAddressLine2"] ?? "";
  fields["locality"] = data["locality"] ?? "";
  fields["province"] = data["province"] ?? "";
  fields["postalCode"] = data["postalCode"] ?? "";
  fields["country"] = data["country"] ?? "CA";
};

const CanadaAddress = (input) => {
  const {
    form,
    getResourceValue,
    context,
    setFormData,
    provinceCodes,
    showCountry = false,
    disabled = false,
    isPOBox,
    streetAddressLine1,
    streetAddressLine2,
    locality,
    province,
    postalCode,
  } = input;

  const setFormValue = (name, value) => {
    const newValue = value ?? "";
    form.setFieldValue(name, newValue);

    if (setFormData) {
      const formValue = { ...form.values, [name]: value };
      setFormData(formValue);
    }
  };

  const setFormValues = (values) => {
    Object.entries(values).forEach(([name, value]) => {
      form.setFieldValue(name, value ?? "");
    });

    if (setFormData) {
      // Use form.values as the base, but merge all new values in one shot
      const formValue = { ...form.values, ...values };
      setFormData(formValue);
    }
  };

  let exprDisabledValue = undefined;
  let isPOBoxValue = undefined;
  let streetAddressLine1Value = undefined;
  let streetAddressLine2Value = undefined;
  let localityValue = undefined;
  let provinceValue = undefined;
  let postalCodeValue = undefined;

  if (disabled) {
    exprDisabledValue = !!jexl.evalSync(disabled, {
      ...context,
      form: form.values,
    });
  }

  useEffect(() => {
    if (isPOBox) {
      isPOBoxValue = jexl.evalSync(isPOBox, {
        ...context,
        form: form.values,
      });
      if (
        !form.values.isPOBox &&
        ((!!context?.stepData?.isPOBox && !form.dirty) || !!isPOBoxValue)
      ) {
        setFormValue(
          "isPOBox",
          exprDisabledValue
            ? isPOBoxValue
            : context?.stepData?.isPOBox || isPOBoxValue,
        );
      }
    }

    if (streetAddressLine1) {
      streetAddressLine1Value = jexl.evalSync(streetAddressLine1, {
        ...context,
        form: form.values,
      });
      if (
        !form.values.streetAddressLine1 &&
        ((!!context?.stepData?.streetAddressLine1 && !form.dirty) ||
          !!streetAddressLine1Value)
      ) {
        setFormValue(
          "streetAddressLine1",
          exprDisabledValue
            ? streetAddressLine1Value
            : context?.stepData?.streetAddressLine1 || streetAddressLine1Value,
        );
      }
    }

    if (streetAddressLine2) {
      streetAddressLine2Value = jexl.evalSync(streetAddressLine2, {
        ...context,
        form: form.values,
      });
      if (
        !form.values.streetAddressLine2 &&
        ((!!context?.stepData?.streetAddressLine2 && !form.dirty) ||
          !!streetAddressLine2Value)
      ) {
        setFormValue(
          "streetAddressLine2",
          exprDisabledValue
            ? streetAddressLine2Value
            : context?.stepData?.streetAddressLine2 || streetAddressLine2Value,
        );
      }
    }

    if (locality) {
      localityValue = jexl.evalSync(locality, {
        ...context,
        form: form.values,
      });
      if (
        !form.values.locality &&
        ((!!context?.stepData?.locality && !form.dirty) || !!localityValue)
      ) {
        setFormValue(
          "locality",
          exprDisabledValue
            ? localityValue
            : context?.stepData?.locality || localityValue,
        );
      }
    }

    if (province) {
      provinceValue =
        jexl.evalSync(province, { ...context, form: form.values }) || "CA-AB";
      if (
        !form.values.province &&
        ((!!context?.stepData?.province && !form.dirty) || !!provinceValue)
      ) {
        setFormValue(
          "province",
          exprDisabledValue
            ? provinceValue
            : context?.stepData?.province || provinceValue,
        );
      }
    }

    if (postalCode) {
      postalCodeValue = jexl.evalSync(postalCode, {
        ...context,
        form: form.values,
      });
      if (
        !form.values.postalCode &&
        ((!!context?.stepData?.postalCode && !form.dirty) || !!postalCodeValue)
      ) {
        setFormValue(
          "postalCode",
          exprDisabledValue
            ? postalCodeValue
            : context?.stepData?.postalCode || postalCodeValue,
        );
      }
    }
  }, [form]);

  useEffect(() => {
    if (!!provinceCodes) {
      const value = `CA-${provinceCodes.split(",")[0]}`;
      setFormValue("province", value);
    }
  }, []);

  const handleBlur = (form, name) => {
    form.setFieldTouched(name, true);
  };

  const handleChange = (form, name, value, titleCase = false) => {
    delete form.errors[name];
    const formattedValue = titleCase ? toTitleCase(value) : value;
    setFormValue(name, formattedValue);
  };

  const handleIsPOBoxChange = (form, name, value) => {
    delete form.errors[name];

    // If isPOBox is changed to "Yes", clear streetAddressLine2
    if (value === "Y") {
      // Clear PO Box mismatch error if isPOBox is changed to "Y"
      if (
        form.errors["streetAddressLine1"] ===
        STREET_ADDRESS_POBOX_ERROR_RESOURCE_STRING
      ) {
        delete form.errors["streetAddressLine1"];
      }

      delete form.errors["streetAddressLine2"];

      setFormValues({
        streetAddressLine2: "",
        [name]: value,
      });
    } else {
      setFormValue(name, value);
    }
  };

  const handleDropdownChange = (form, name, value) => {
    delete form.errors[name];
    form.setFieldTouched(name, true);
    setFormValue(name, value);
  };

  const handlePostalCodeChange = (form, name, value) => {
    delete form.errors[name];
    const formattedValue = formatPostalCode(value);
    setFormValue(name, formattedValue);
  };

  const handlePopulate = (address) => {
    const updates = {
      streetAddressLine1: address.streetAddressLine1,
      streetAddressLine2: address.streetAddressLine2,
      locality: address.locality,
      province: address.province,
      postalCode: address.postalCode,
    };

    setFormValues(updates);
  };

  return (
    <>
      <GoabSpacer vSpacing="l" />
      <p>{getResourceValue({ key: "RESOURCES@form.searchAddress" })}</p>
      <GoabSpacer vSpacing="l" />
      <GoabGrid minChildWidth="200px">
        <GoabFormItem
          label={getResourceValue({ key: "RESOURCES@form.findAddress.label" })}
          helpText={getResourceValue({
            key: "RESOURCES@form.findAddress.helpText",
          })}
        >
          <CanadaPostGoabInput
            width="45%"
            onPopulate={handlePopulate}
            provinceCodes={provinceCodes}
            disabled={exprDisabledValue}
            form={form}
          />
        </GoabFormItem>
      </GoabGrid>
      <GoabSpacer vSpacing="l" />

      {!showCountry && (
        <>
          <GoabFormItem
            label={getResourceValue({
              key: "RESOURCES@form.isPOBox.label",
            })}
            error={
              form.errors.isPOBox &&
              form.touched.isPOBox &&
              getResourceValue({
                key: form.errors.isPOBox,
              })
            }
            id="isPOBox"
          >
            <GoabRadioGroup
              orientation="horizontal"
              name="isPOBox"
              value={form.values.isPOBox || isPOBoxValue}
              onChange={({ name, value }) =>
                handleIsPOBoxChange(form, name, value)
              }
              onBlur={({ name }) => handleBlur(form, name)}
              error={
                form.errors.isPOBox &&
                form.touched.isPOBox &&
                getResourceValue({
                  key: form.errors.isPOBox,
                })
              }
            >
              <GoabRadioItem value="Y" label="Yes">Yes</GoabRadioItem>
              <GoabRadioItem value="N" label="No">No</GoabRadioItem>
            </GoabRadioGroup>
          </GoabFormItem>
          <GoabSpacer vSpacing="l" />
          <GoabGrid minChildWidth="200px">
            <GoabFormItem
              label={getResourceValue({
                key: "RESOURCES@form.streetAddressLine1.label",
              })}
              error={
                form.errors.streetAddressLine1 &&
                form.touched.streetAddressLine1 &&
                getResourceValue({
                  key: form.errors.streetAddressLine1,
                })
              }
              id="streetAddressLine1"
            >
              <GoabInputText
                name="streetAddressLine1"
                maxLength="50"
                disabled={exprDisabledValue}
                value={
                  form.values.streetAddressLine1 || streetAddressLine1Value
                }
                error={
                  form.errors.streetAddressLine1 &&
                  form.touched.streetAddressLine1 &&
                  getResourceValue({
                    key: form.errors.streetAddressLine1,
                  })
                }
                onChange={({ name, value }) =>
                  handleChange(form, name, value, true)
                }
                onBlur={({ name }) => handleBlur(form, name)}
              />
            </GoabFormItem>
            <GoabFormItem
              label={getResourceValue({
                key: "RESOURCES@form.streetAddressLine2.label",
              })}
              requirement="optional"
              error={
                form.errors.streetAddressLine2 &&
                form.touched.streetAddressLine2 &&
                getResourceValue({
                  key: form.errors.streetAddressLine2,
                })
              }
              id="streetAddressLine2"
            >
              <GoabInputText
                name="streetAddressLine2"
                maxLength="50"
                disabled={exprDisabledValue || form.values.isPOBox === "Y"}
                value={
                  form.values.streetAddressLine2 || streetAddressLine2Value
                }
                error={
                  form.errors.streetAddressLine2 &&
                  form.touched.streetAddressLine2 &&
                  getResourceValue({
                    key: form.errors.streetAddressLine2,
                  })
                }
                onChange={({ name, value }) =>
                  handleChange(form, name, value, true)
                }
                onBlur={({ name }) => handleBlur(form, name)}
              />
            </GoabFormItem>
          </GoabGrid>
          <GoabSpacer vSpacing="l" />
        </>
      )}
      <GoabGrid minChildWidth="200px">
        <GoabFormItem
          label={getResourceValue({ key: "RESOURCES@form.locality.label" })}
          error={
            form.errors.locality &&
            form.touched.locality &&
            getResourceValue({ key: form.errors.locality })
          }
          id="locality"
        >
          <GoabInputText
            name="locality"
            maxLength="40"
            disabled={exprDisabledValue}
            value={form.values.locality || localityValue}
            error={
              form.errors.locality &&
              form.touched.locality &&
              getResourceValue({ key: form.errors.locality })
            }
            onChange={({ name, value }) =>
              handleChange(form, name, value, true)
            }
            onBlur={({ name }) => handleBlur(form, name)}
          />
        </GoabFormItem>
        <GoabFormItem
          label={getResourceValue({
            key: showCountry
              ? "RESOURCES@form.provinceState.label"
              : "RESOURCES@form.province.label",
          })}
          error={
            form.errors.province &&
            form.touched.province &&
            getResourceValue({ key: form.errors.province })
          }
          id="province"
        >
          {!showCountry && (
            <GoabDropdown
              name="province"
              placeholder={getResourceValue({
                key: "RESOURCES@global.select.placeholder",
              })}
              disabled={
                (exprDisabledValue && form.values.province) || !!provinceCodes
              }
              value={form.values.province || provinceValue || "CA-AB"}
              error={
                form.errors.province &&
                form.touched.province &&
                getResourceValue({ key: form.errors.province })
              }
              onChange={({ name, value }) =>
                handleDropdownChange(form, name, value)
              }
              width="290px"
              arialabelledby="province"
              required="true"
            >
              {(provinceList ?? []).map(({ code, label }) => {
                return (
                  <GoabDropdownItem key={code} value={code} label={label} />
                );
              })}
            </GoabDropdown>
          )}
          {showCountry && (
            <GoabInputText
              name="province"
              value={form.values.province || provinceValue}
              disabled={exprDisabledValue}
              error={
                form.errors.province &&
                form.touched.province &&
                getResourceValue({ key: form.errors.province })
              }
              onChange={({ name, value }) => handleChange(form, name, value)}
              onBlur={({ name }) => handleBlur(form, name)}
            />
          )}
        </GoabFormItem>
      </GoabGrid>
      <GoabSpacer vSpacing="l" />
      <GoabGrid minChildWidth="200px">
        {!showCountry && (
          <GoabFormItem
            label={getResourceValue({
              key: "RESOURCES@form.postalCode.label",
            })}
            error={
              form.errors.postalCode &&
              form.touched.postalCode &&
              getResourceValue({ key: form.errors.postalCode })
            }
            id="postalCode"
          >
            <GoabInputText
              name="postalCode"
              width="29ch"
              value={form.values.postalCode || postalCodeValue}
              disabled={exprDisabledValue}
              error={
                form.errors.postalCode &&
                form.touched.postalCode &&
                getResourceValue({ key: form.errors.postalCode })
              }
              onChange={({ name, value }) =>
                handlePostalCodeChange(form, name, value)
              }
              onBlur={({ name }) => handleBlur(form, name)}
              maxLength={7}
            />
          </GoabFormItem>
        )}
        {showCountry && (
          <GoabFormItem
            label={getResourceValue({ key: "RESOURCES@form.country.label" })}
            error={
              form.errors.country &&
              form.touched.country &&
              getResourceValue({ key: form.errors.country })
            }
            id="country"
          >
            <GoabDropdown
              name="country"
              filterable
              placeholder={getResourceValue({
                key: "RESOURCES@global.select.placeholder",
              })}
              disabled={exprDisabledValue}
              value={form.values.country}
              onChange={({ name, value }) => handleChange(form, name, value)}
              onBlur={({ name }) => handleBlur(form, name)}
              width="290px"
              arialabelledby="country"
              required="true"
            >
              {(getCountries() ?? []).map(({ code, label }) => {
                return (
                  <GoabDropdownItem key={code} value={code} label={label} />
                );
              })}
            </GoabDropdown>
          </GoabFormItem>
        )}
      </GoabGrid>
    </>
  );
};

export default CanadaAddress;
